import * as BuyIntentionModels from 'model/BuyIntention';
import { useDispatch, useSelector } from 'react-redux';
import * as buyIntenteionSelectors from 'store/state/buyIntention/buyIntenteionSelectors';
import * as MyOrderIntentionEditCalls from 'store/state/myOrderIntentionEdit/apiCalls';
import * as MyOrderIntentionEditSelectors from 'store/state/myOrderIntentionEdit/myOrderIntentionEditSelectors';
import { actions as MyOrderIntentionEditActions } from 'store/state/myOrderIntentionEdit/myOrderIntentionEditSlice';

type Item = BuyIntentionModels.BuyIntentionData;

export function useEditIntentionItemActions() {
  const dispatch = useDispatch();
  const currentStep = useSelector(MyOrderIntentionEditSelectors.getCurrentStep);
  const customer = useSelector(buyIntenteionSelectors.getCustomer);

  const changeOfferInput = (value: unknown, property: string, record: Item) => {
    if (record.id) {
      if (currentStep === 1) {
        dispatch(
          MyOrderIntentionEditActions.setOverview({
            value,
            property,
            id: record.id,
          }),
        );
      }
      if (!record.productPreference && record.site) {
        dispatch(MyOrderIntentionEditActions.setIdSelectedNow(record.id));
        dispatch(
          MyOrderIntentionEditCalls.fetchPacking({
            siteId: record.site.id,
            productId: record.productId,
          }),
        );
        dispatch(MyOrderIntentionEditActions.setIsLoading(true));
      }
    }
  };

  const setOverviewTotal = (record: Item, value: number) => {
    if (currentStep === 1) {
      dispatch(
        MyOrderIntentionEditActions.setTotalPriceOverview({
          id: record.id,
          value,
        }),
      );
    }
  };

  const choicePacking = (packingCode: string, offer: Item) => {
    // The packing list comes from the same options the Select shows.
    const composition = offer.productPreference.find(
      (packing) => packing.code === packingCode,
    ) as BuyIntentionModels.PackingFull;
    changeOfferInput(composition, 'packingSelected', offer);
    changeOfferInput(composition.qe, 'quantityPacking', offer);
    changeOfferInput(composition.qpe, 'quantityUnit', offer);

    const totalpriceCalc =
      composition.qpe *
      composition.qe *
      (Number(offer.unitPrice) +
        Number(
          composition.additionalCost === 'S' ? composition.packingCost : 0,
        ) +
        Number(
          offer.shippingFeeFilials !== null
            ? offer.shippingFeeFilials.productShippingValue
            : 0,
        ));

    changeOfferInput(totalpriceCalc, 'totalPrice', offer);
    setOverviewTotal(offer, totalpriceCalc);
  };

  const choiceProducer = (producerId: number, record: Item) => {
    const producer = record.siteList?.find(
      (item) => item.id === producerId,
    ) as BuyIntentionModels.Producer;
    dispatch(
      MyOrderIntentionEditActions.setOverview({
        value: producer,
        property: 'site',
        id: record.id,
      }),
    );

    dispatch(MyOrderIntentionEditActions.setIdSelectedNow(record.id));
    dispatch(
      MyOrderIntentionEditCalls.fetchPacking({
        siteId: producer.id,
        productId: record.productId,
      }),
    );
    dispatch(MyOrderIntentionEditActions.setIsLoading(true));
  };

  const recalculate = (
    record: Item,
    quantityPacking: number,
    quantityUnit: number,
  ) => {
    if (record.unitPrice > 0) {
      const price = record.unitPrice;
      const totalpriceCalc =
        quantityPacking *
        quantityUnit *
        (Number(price) +
          Number(
            record.packingSelected.additionalCost === 'S'
              ? record.packingSelected.packingCost
              : 0,
          ) +
          Number(
            record.shippingFeeFilials !== null
              ? record.shippingFeeFilials.productShippingValue
              : 0,
          ));
      changeOfferInput(totalpriceCalc, 'totalPrice', record);
      changeOfferInput(price, 'unitPriceNumber', record);
      setOverviewTotal(record, totalpriceCalc);
    } else {
      changeOfferInput(0, 'totalPrice', record);
    }
  };

  const changeQuantityPacking = (value: number, record: Item) => {
    recalculate(record, value, record.quantityUnit);
    changeOfferInput(value, 'quantityPacking', record);
  };

  const changeQuantityUnit = (value: number, record: Item) => {
    recalculate(record, record.quantityPacking, value);
    changeOfferInput(value, 'quantityUnit', record);
  };

  const changeUnitPrice = (value: number, record: Item) => {
    changeOfferInput(value, 'unitPrice', record);
    if (value > 0) {
      const price = value;
      const totalpriceCalc =
        record.quantityUnit *
        record.quantityPacking *
        (price +
          (record.packingSelected?.additionalCost === 'S'
            ? record.packingSelected?.packingCost
            : 0) +
          (record.shippingFeeFilials !== null
            ? record.shippingFeeFilials.productShippingValue
            : 0));
      changeOfferInput(totalpriceCalc, 'totalPrice', record);
      changeOfferInput(price, 'unitPriceNumber', record);
      setOverviewTotal(record, totalpriceCalc);
    } else {
      changeOfferInput(0, 'totalPrice', record);
    }
  };

  return {
    customer,
    changeOfferInput,
    choicePacking,
    choiceProducer,
    changeQuantityPacking,
    changeQuantityUnit,
    changeUnitPrice,
  };
}

export type EditIntentionItemActions = ReturnType<
  typeof useEditIntentionItemActions
>;
